import {
  createListItem,
  deleteListItem,
  getListItems,
  updateListItem,
} from '../graph/lists';
import { LISTS, READ_CACHE_MS } from './listNames';
import { toStore, type StoreFields } from './mappers';
import { scopeStores } from './scope';
import type { AppUser, Store } from './types';

function toFields(store: Omit<Store, 'id'>): StoreFields {
  return {
    Title: store.name,
    StoreNumber: store.storeNumber,
    District: store.district,
    Region: store.region,
    StateCode: store.state,
    Address: store.address,
    City: store.city,
    Zip: store.zip,
    StorePhone: store.phone,
    OwnerId: store.ownerId,
    HubSite: store.hubSite,
    ManagerEmail: store.managerEmail,
    DmName: store.dmName,
    DmUpn: store.dmUpn,
    Active: store.active,
    ParkingOnSite:
      store.parking === 'unknown'
        ? ''
        : store.parking === 'yes'
          ? 'Yes'
          : 'No',
    ElevatorCount: store.elevatorCount,
    LongDistance: store.longDistance,
  };
}

export interface StoreQueryOptions {
  /**
   * Design rule: inactive (divested) stores never appear by default on any
   * screen. Surfaces that need them (e.g. the admin filter) must opt in.
   */
  includeInactive?: boolean;
}

/** Every store, unscoped — for Admin surfaces only. Active-only by default. */
export async function getAllStores(
  options: StoreQueryOptions = {},
): Promise<Store[]> {
  // Sort client-side rather than $orderby: the portfolio is ~80 rows, and
  // server-side ordering would couple this query to the column's index.
  const items = await getListItems<StoreFields>(LISTS.stores, { cacheMs: READ_CACHE_MS });
  return items
    .map((i) => toStore(i.id, i.fields))
    .filter((s) => options.includeInactive || s.active)
    .sort((a, b) =>
      a.storeNumber.localeCompare(b.storeNumber, undefined, { numeric: true }),
    );
}

/**
 * Stores visible to the signed-in user (role scoping applied here — the
 * portfolio is ~80 rows, so we fetch once and filter in the data layer).
 * Active-only by default, like every store read.
 */
export async function getVisibleStores(
  user: AppUser,
  options: StoreQueryOptions = {},
): Promise<Store[]> {
  return scopeStores(user, await getAllStores(options));
}

export async function createStore(store: Omit<Store, 'id'>): Promise<Store> {
  const created = await createListItem<StoreFields>(
    LISTS.stores,
    toFields(store),
  );
  return toStore(created.id, created.fields);
}

export async function updateStore(store: Store): Promise<void> {
  await updateListItem<StoreFields>(LISTS.stores, store.id, toFields(store));
}

export async function deleteStore(id: string): Promise<void> {
  await deleteListItem(LISTS.stores, id);
}
